import type { ReactElement } from 'react';

import * as Dialog from '@radix-ui/react-dialog';
import { useStackModal } from '@rango-dev/internal-profile/hooks';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getContainer } from '../../utils/generalFunctions/common';

import modalStyles from './Modal.module.css';
import styles from './ModalStack.module.css';

/*
 * Fallback removal for a leaving panel, in case its exit `animationend` never
 * fires (e.g. prefers-reduced-motion disables the animation).
 */
const LEAVE_FALLBACK_MS = 400;

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/*
 * Wallet flows (Reown AppKit / WalletConnect, TON Connect, …) render their own
 * modal on top of ours, outside this Dialog. Our Dialog focus-traps its content,
 * so focus can't tell us it's covered; and the overlay is often a Web Component
 * with a shadow DOM, so we can't reliably query for it by tag/class either.
 * Instead, hit-test the viewport centre: `elementFromPoint` returns the topmost
 * element (the host element, for a shadow-DOM overlay). If that element isn't
 * inside our stack container, a foreign overlay is on top and should own Escape.
 */
function isCoveredByForeignOverlay(container: HTMLElement | null): boolean {
  if (!container || typeof document === 'undefined') {
    return false;
  }
  const topElement = document.elementFromPoint(
    Math.floor(window.innerWidth / 2),
    Math.floor(window.innerHeight / 2),
  );
  return !!topElement && !container.contains(topElement);
}

type StackItem = ReturnType<typeof useStackModal>['stack'][number];

/** Rendered entry — kept mounted while `leaving` so it can animate out. */
type StackEntry = {
  item: StackItem;
  leaving: boolean;
};

/**
 * Renders the profile `useStackModal` stack with the Radix chrome from
 * `newModal` — one shared Radix Dialog owns the backdrop, scroll-lock, focus
 * trap and Escape, and each stack entry becomes a peeked panel (full-screen
 * sheet on mobile, right sidebar on desktop). It replaces the legacy Stitches
 * `StackModal` while keeping the same `useStackModal` push/pop/clear API, so
 * every existing caller keeps working. It is rendered by `Layout` inside its
 * `RadixThemeProvider`, so the panels — and any nested Radix Themes overlays
 * they render — inherit the app Theme as a non-root theme.
 */
export function ProfileStackModal(): ReactElement {
  const { t } = useTranslation();
  const stackModal = useStackModal();
  const [entries, setEntries] = useState<StackEntry[]>([]);
  /*
   * `onExit` must fire exactly once per item. Its removal can be triggered by
   * both the exit `animationend` and the fallback timer (and React strict mode
   * double-invokes effects), so guard against a repeat call.
   */
  const exitedIds = useRef<Set<string>>(new Set());

  /*
   * Reconcile the logical stack into render entries: present items first (in
   * stack order), then any items that have left the stack, marked `leaving` so
   * they stay mounted on top and animate out before being removed.
   */
  useEffect(() => {
    setEntries((previous) => {
      const stackIds = new Set(stackModal.stack.map((item) => item.id));
      /*
       * Clear the exit guard for ids currently in the stack so a re-pushed id
       * (e.g. reopening a modal that reuses its id) can fire `onExit` again.
       */
      stackIds.forEach((id) => exitedIds.current.delete(id));
      const leaving = previous
        .filter((entry) => !stackIds.has(entry.item.id))
        .map((entry) => ({ item: entry.item, leaving: true }));
      const present = stackModal.stack.map((item) => ({ item, leaving: false }));
      return present.concat(leaving);
    });
  }, [stackModal.stack]);

  const removeEntry = useCallback((item: StackItem) => {
    setEntries((previous) => previous.filter((entry) => entry.item.id !== item.id));
    if (!exitedIds.current.has(item.id)) {
      exitedIds.current.add(item.id);
      item.onExit?.();
    }
  }, []);

  // Safety net so leaving panels are always removed even without an animation.
  useEffect(() => {
    const timers = entries
      .filter((entry) => entry.leaving)
      .map((entry) => setTimeout(() => removeEntry(entry.item), LEAVE_FALLBACK_MS));
    return () => timers.forEach(clearTimeout);
  }, [entries, removeEntry]);

  const open = entries.length > 0;
  const presentEntries = entries.filter((entry) => !entry.leaving);
  const presentCount = presentEntries.length;
  const topId = presentCount > 0 ? presentEntries[presentCount - 1].item.id : undefined;

  /*
   * The Dialog runs non-modal (see `modal={false}` below) so wallet/social flows
   * can open their own focus-requiring modals (Privy, WalletConnect) on top —
   * a modal Dialog would trap focus and block typing in them. Non-modal drops
   * Radix's scroll lock, so lock the app's scroll container ourselves while the
   * stack is open. (Plain `overflow: hidden`, unlike Radix's react-remove-scroll,
   * doesn't preventDefault the wheel, so those foreign modals still scroll.)
   */
  useEffect(() => {
    const container = getContainer();
    if (!open || !container) {
      return;
    }
    const previousOverflow = container.style.overflow;
    container.style.overflow = 'hidden';
    return () => {
      container.style.overflow = previousOverflow;
    };
  }, [open]);

  /*
   * Keep the top panel interactive/focused and make the panels behind it inert.
   * `inert` (rather than `aria-hidden`) removes them from focus and the a11y
   * tree without the "aria-hidden on a focused ancestor" warning. On open Radix
   * auto-focuses the first control; pushing/popping swaps the top panel without
   * re-firing that, so move focus to the new top's first focusable when it
   * changes.
   */
  useEffect(() => {
    const container = getContainer();
    if (!container) {
      return;
    }
    container.querySelectorAll<HTMLElement>('[data-modal-panel-id]').forEach((panel) => {
      panel.inert = panel.getAttribute('data-modal-panel-id') !== topId;
    });
    if (!topId) {
      return;
    }
    const topPanel = container.querySelector<HTMLElement>(`[data-modal-panel-id="${topId}"]`);
    const focusable = topPanel?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (focusable ?? topPanel)?.focus();
  }, [topId]);

  const handleViewportClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Clicking the empty backdrop area (not a panel) closes the whole stack.
    if (event.target === event.currentTarget) {
      stackModal.clear();
    }
  };

  return (
    <Dialog.Root
      open={open}
      modal={false}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          /*
           * A wallet flow's own modal on top can make Radix request a close
           * (its Escape / outside handling). Ignore it while that overlay covers
           * us so our stack stays put; the overlay owns its own dismissal.
           */
          if (isCoveredByForeignOverlay(getContainer())) {
            return;
          }
          stackModal.clear();
        }
      }}>
      <Dialog.Portal container={getContainer()}>
        {/*
         * A plain backdrop, not `Dialog.Overlay` — the Radix overlay only renders
         * for a modal Dialog, and ours is non-modal (see `modal={false}` above).
         */}
        <div className={styles.overlay} />
        <Dialog.Content
          asChild
          aria-describedby={undefined}
          onEscapeKeyDown={(event) => {
            /*
             * Always stop Radix's default (which would close the whole stack).
             * Escape steps back one level — UNLESS a wallet flow has opened its
             * own overlay on top (e.g. the WalletConnect modal): then let that
             * overlay handle Escape and leave our stack untouched.
             */
            event.preventDefault();
            if (isCoveredByForeignOverlay(getContainer())) {
              return;
            }
            stackModal.pop();
          }}
          onInteractOutside={(event) => {
            /*
             * Don't let Radix's default "close on any outside interaction" fire:
             * wallet flows open their own overlays outside this Dialog (e.g. the
             * WalletConnect QR modal, portalled to <body>), and clicking those
             * would otherwise clear the whole stack. Backdrop dismissal is still
             * handled by the viewport's own click handler below.
             */
            event.preventDefault();
          }}>
          <div className={styles.viewport} onClick={handleViewportClick}>
            {/*
             * Accessible name for the dialog — screen readers announce this
             * when the stack opens. Each panel renders its own visible
             * heading, so it stays a generic, visually-hidden label.
             */}
            <Dialog.Title className={styles.srOnly}>{t('Modal')}</Dialog.Title>
            {entries.map((entry, index) => {
              const depth = entry.leaving ? 0 : presentCount - 1 - index;
              return (
                <div
                  key={entry.item.id}
                  data-modal-panel-id={entry.item.id}
                  tabIndex={-1}
                  className={`${modalStyles.panel} ${styles.panel}`}
                  data-depth={Math.min(depth, 2)}
                  data-leaving={entry.leaving ? 'true' : undefined}
                  onAnimationEnd={(event) => {
                    if (entry.leaving && event.target === event.currentTarget) {
                      removeEntry(entry.item);
                    }
                  }}>
                  {entry.item.renderContent(index)}
                </div>
              );
            })}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
